import { useState } from "react";
import { useLocalStorage } from "@uidotdev/usehooks";
import { AccessMode, NANGO_INTEGRATION_BY_ACCESS_MODE, StorageLevel } from "../../lib/google-drive-constants";
import { localStorageKeys } from "../../lib/local-storage-keys";
// eslint-disable-next-line no-restricted-imports
import { apiClient } from "../service/tekkr-service";
import { openPopupAndWaitForClose } from "../../lib/open-popup";

export function useGoogleDriveConnectFlow() {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [storageLevel, setStorageLevel] = useState<StorageLevel | null>(null);
    const [accessMode, setAccessMode] = useState<AccessMode | null>(null);
    const [connecting, setConnecting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [, setSyncJobId] = useLocalStorage<string | null>(localStorageKeys.googleDriveSyncJobId, null);

    const openDialog = () => {
        setSuccess(false);
        setDialogOpen(true);
    };

    const connect = async () => {
        try {
            setConnecting(true);

            // Step 1: OAuth via Nango
            console.log("[gdrive] starting connect flow, accessMode:", accessMode, "storageLevel:", storageLevel);
            const integration = NANGO_INTEGRATION_BY_ACCESS_MODE[accessMode!];
            const connectRes = await apiClient.createGoogleDriveConnectSession({
                body: { allowedIntegrations: [integration] },
            });
            if (connectRes.status !== 200) {
                throw new Error("Failed to create connect session");
            }
            await openPopupAndWaitForClose(connectRes.body.connectLink);

            // Pre-open popup for picker (must happen in click context to avoid blocker)
            const pickerPopup =
                accessMode === "restricted"
                    ? window.open("about:blank", "picker-window", "width=800,height=600")
                    : null;

            // Step 2: Setup source config
            const setupRes = await apiClient.setupGoogleDrive({
                body: { storageLevel: storageLevel!, accessMode: accessMode! },
            });
            if (setupRes.status !== 200) {
                pickerPopup?.close();
                throw new Error("Failed to setup Google Drive");
            }

            // Step 3: For restricted mode, navigate picker popup
            if (setupRes.body.pickerUrl && pickerPopup) {
                pickerPopup.location.href = setupRes.body.pickerUrl;
                await new Promise<void>((resolve) => {
                    const interval = setInterval(() => {
                        if (pickerPopup.closed) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 500);
                });

                // Step 4: Trigger sync after picker closes
                const syncRes = await apiClient.setupGoogleDrive({
                    body: { storageLevel: storageLevel! },
                });
                if (syncRes.status === 200 && syncRes.body.jobId) {
                    setSyncJobId(syncRes.body.jobId);
                }
            } else {
                setSyncJobId(setupRes.body.jobId);
            }

            setSuccess(true);
        } finally {
            setConnecting(false);
        }
    };

    const canConnect = !!storageLevel && !!accessMode;

    return {
        dialogOpen,
        setDialogOpen,
        storageLevel,
        setStorageLevel,
        accessMode,
        setAccessMode,
        connecting,
        success,
        isPending: false,
        canConnect,
        openDialog,
        connect,
    };
}
